import React, { useEffect } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Toggle from "@/components/forms/Toggle";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BrushCleaning } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { buildContactDateRange, parseContactDateRange } from "@/utils/contactDateRange";
import { getPipeline } from "@/utils/contactPipeline";

const ORIGIN_OPTIONS = [
  { label: "Campaña", value: "facebook" },
  { label: "WhatsApp", value: "Whatsapp_Button" },
  { label: "Llamada", value: "Call_Button" },
  { label: "PRO CRM", value: "Pro_Crm" },
  { label: "Formulario", value: "Formulario" },
];

const FiltersSchema = Yup.object().shape({
  search: Yup.string(),
  property: Yup.string(),
  status: Yup.string(),
  start_date: Yup.string(),
  end_date: Yup.string(),
  pipeline_id: Yup.string(),
  pipeline_stage: Yup.array().of(Yup.string()),
  origin: Yup.array().of(Yup.string()),
  property_id: Yup.string(),
});

const AutoSubmit = ({ values, submitForm }) => {
  useEffect(() => {
    submitForm(values);
  }, [values, submitForm]);

  return null;
};

AutoSubmit.propTypes = {
  values: PropTypes.object.isRequired,
  submitForm: PropTypes.func.isRequired,
};

const getInitialValues = (initialFilters) => {
  const { startDate, endDate } = parseContactDateRange(initialFilters.created_range);
  const pipelineStages = Array.isArray(initialFilters.pipeline_stage)
    ? initialFilters.pipeline_stage.map(String)
    : String(initialFilters.pipeline_stage ?? "")
        .split(",")
        .filter(Boolean);

  return {
    search: initialFilters.search ?? "",
    property: initialFilters.property ?? "",
    status: String(initialFilters.status ?? "1,2"),
    start_date: startDate ?? "",
    end_date: endDate ?? "",
    created_range: initialFilters.created_range ?? "",
    pipeline_id: String(initialFilters.pipeline_id ?? ""),
    pipeline_stage: pipelineStages,
    origin: initialFilters.origin ?? [],
    property_id: initialFilters.property_id ?? "",
  };
};

export default function Filters({ onSubmit, onReset, managedLeads, pipelines, initialFilters }) {
  const pipeline = getPipeline(pipelines, initialFilters?.pipeline_id);
  const stages = pipeline?.stages ?? [];

  const submit = (values) => {
    const v = { ...values };
    Object.keys(v).forEach((key) => {
      if (v[key] === "") {
        delete v[key];
      }
    });

    const createdRange = buildContactDateRange(v.start_date, v.end_date, v.created_range);
    if (createdRange) {
      v.created_range = createdRange;
    } else {
      delete v.created_range;
    }
    delete v.start_date;
    delete v.end_date;
    if (v.origin && v.origin.length > 0) {
      const contactPlaces = [];
      const utmCampaigns = [];

      v.origin.forEach((val) => {
        if (val === "facebook") contactPlaces.push(val);
        else utmCampaigns.push(val);
      });

      if (contactPlaces.length > 0) v.contact_place = contactPlaces.join(",");
      if (utmCampaigns.length > 0) v.utm_campaign = utmCampaigns.join(",");
      delete v.origin;
    }

    v.page = 1;
    onSubmit(v);
  };

  return (
    <div>
      <Formik
        initialValues={getInitialValues(initialFilters)}
        validationSchema={FiltersSchema}
        onSubmit={submit}
      >
        {({ values, resetForm, setFieldValue, setValues, submitForm }) => (
          <Form>
            <AutoSubmit values={values} submitForm={submitForm} />
            <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-3 items-end">
              <div>
                <Field name="search">
                  {({ field }) => (
                    <Input {...field} placeholder="Nombre, email o teléfono" className="w-full" />
                  )}
                </Field>
                <ErrorMessage component="span" className="input-error" name="search" />
              </div>

              <div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between">
                      {values.pipeline_stage.length > 0
                        ? `${values.pipeline_stage.length} seleccionados`
                        : "Estatus"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[200px] max-h-60 overflow-y-auto">
                    {stages.map((stage) => {
                      const stageId = String(stage.id);
                      const isChecked = values.pipeline_stage.includes(stageId);

                      return (
                        <DropdownMenuCheckboxItem
                          key={stageId}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            setValues((current) => ({
                              ...current,
                              pipeline_id: String(pipeline.id),
                              pipeline_stage: checked
                                ? [...current.pipeline_stage, stageId]
                                : current.pipeline_stage.filter((id) => id !== stageId),
                            }));
                          }}
                        >
                          {stage.name}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                    {stages.length === 0 && (
                      <p role="status" className="px-2 py-1.5 text-sm text-muted-foreground">
                        {!pipelines
                          ? "Cargando etapas…"
                          : initialFilters?.pipeline_id && !pipeline
                            ? "No se encontró el pipeline seleccionado."
                            : "No hay etapas disponibles."}
                      </p>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between">
                      {values.origin.length > 0
                        ? `${values.origin.length} seleccionados`
                        : "Origen"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[200px] max-h-60 overflow-y-auto">
                    {ORIGIN_OPTIONS.map((option) => {
                      const isChecked = values.origin.includes(option.value);

                      return (
                        <DropdownMenuCheckboxItem
                          key={option.value}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            const newValue = checked
                              ? [...values.origin, option.value]
                              : values.origin.filter((val) => val !== option.value);
                            setFieldValue("origin", newValue);
                          }}
                        >
                          {option.label}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div>
                <Field name="property_id">
                  {({ field, form }) => (
                    <Input
                      {...field}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="ID Propiedad"
                      className="w-full"
                      onChange={(e) => {
                        const value = e.target.value;
                        const numericValue = value.replace(/\D/g, "");
                        form.setFieldValue(field.name, numericValue);
                      }}
                    />
                  )}
                </Field>
              </div>

              <div className="relative">
                <DatePicker
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  placeholderText="Periodo de contactos"
                  selectsRange={true}
                  startDate={values.start_date}
                  endDate={values.end_date}
                  onChange={([start_date, end_date]) => {
                    setFieldValue("start_date", start_date);
                    setFieldValue("end_date", end_date);
                  }}
                />
              </div>

              {managedLeads && (
                <div className="flex items-center space-x-2 h-9">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    Sólo perfilados
                  </span>
                  <Toggle
                    type="checkbox"
                    name="only_managed_leads"
                    className="toggle-input"
                    status={values.only_managed_leads}
                    onChange={(e) => {
                      setFieldValue("only_managed_leads", e);
                      setFieldValue("managed_leads", managedLeads);
                    }}
                  />
                </div>
              )}

              <div className="flex h-9">
                <Button
                  variant="outline"
                  type="button"
                  className="w-full"
                  onClick={() => {
                    resetForm({ values: getInitialValues({}) });
                    onReset();
                  }}
                >
                  <BrushCleaning className="mr-2 h-4 w-4" />
                  Limpiar
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

Filters.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  managedLeads: PropTypes.bool,
  pipelines: PropTypes.array,
  initialFilters: PropTypes.object,
};

Filters.defaultProps = {
  onSubmit: () => {},
  onReset: () => {},
  pipelines: [],
  initialFilters: {},
};
